import {Injectable} from '@angular/core';
import {forkJoin, map, Observable, of, switchMap} from 'rxjs';
import {BookListEntry, V3ProductShare} from '../../../model/v3/drm';
import {AppRestV3Service} from '../../v3/app-rest-v3.service';
import {BooksNativeServiceApiV3} from '../books-native-api-v3';

/**
 * Web implementation of `BooksNativeServiceApiV3` - reads the books data directly
 * from the v3 api (API Gateway / EventsDrm) through `AppRestV3Service`.
 */
@Injectable({
    providedIn: 'root'
})
export class WebNativeServiceApiV3 implements BooksNativeServiceApiV3 {

    constructor(private appRestV3: AppRestV3Service) {}

    /**
     * An account may hold several shares of the same product (e.g. several purchases), so the
     * share rows are grouped by product - one list entry per book, with the slots summed up.
     */
    getProducts(): Observable<BookListEntry[]> {
        return this.appRestV3.getShares().pipe(
            map(page => this.groupByProduct(page.content ?? []).map(shares => ({
                share: this.mergeShares(shares),
                hasProduct: true
            })))
        )
    }

    getProductById(productId: number): Observable<BookListEntry> {
        return this.appRestV3.getShares(productId).pipe(
            map(page => page.content ?? []),
            switchMap(shares => shares.length
                ? forkJoin(shares.map(share => this.appRestV3.getDownloads(share.id))).pipe(
                    map(downloads => ({
                        share: this.mergeShares(shares),
                        hasProduct: true,
                        usedDevices: downloads.reduce((sum, list) => sum + list.length, 0)
                    })))
                : this.appRestV3.getProducts([productId]).pipe(
                    map(page => {
                        const product = page.content?.[0]
                        if (!product) throw new Error(`product ${productId} not found`)
                        return {
                            share: {id: null, product: product, emptySlots: 0} as V3ProductShare,
                            hasProduct: false,
                            usedDevices: 0
                        }
                    })))
        )
    }

    getImageUrl(productId: number, releaseId?: number): Observable<string> {
        return this.appRestV3.getCoverObjectUrl(productId)
    }

    private groupByProduct(shares: V3ProductShare[]): V3ProductShare[][] {
        const byProduct = new Map<number, V3ProductShare[]>()
        shares.forEach(share => {
            const group = byProduct.get(share.product.id)
            if (group) group.push(share)
            else byProduct.set(share.product.id, [share])
        })
        return Array.from(byProduct.values())
    }

    /**
     * One representative share (the first) with `emptySlots` summed over all shares of the product.
     */
    private mergeShares(shares: V3ProductShare[]): V3ProductShare {
        return {
            ...shares[0],
            emptySlots: shares.reduce((sum, share) => sum + (share.emptySlots ?? 0), 0)
        }
    }
}
